import React, { useMemo } from 'react';
import { OutcomeSection as OutcomeSectionType } from '../../../../../types/case-workspace';
import { Badge } from '../../../../ui/badge';

interface OutcomeSectionProps {
  outcome: OutcomeSectionType;
  onDecisionClick?: (decisionIdOrReference: string) => void;
}

const OutcomeSection: React.FC<OutcomeSectionProps> = ({ outcome }) => {
  const outcomeMeta = useMemo(() => {
    if (outcome.outcome === 'partially_upheld') {
      return {
        label: 'Partially upheld',
        badgeVariant: 'partiallyUpheld' as const,
      };
    }

    if (outcome.outcome === 'not_upheld') {
      return {
        label: 'Not upheld',
        badgeVariant: 'notUpheld' as const,
      };
    }

    return {
      label: 'Upheld',
      badgeVariant: 'upheld' as const,
    };
  }, [outcome.outcome]);

  const compactRationale =
    outcome.rationale && outcome.rationale.trim().length > 0 && outcome.rationale.trim().length <= 220
      ? outcome.rationale.trim()
      : null;

  return (
    <section className="border-t border-border bg-[var(--go-spring)] px-6 py-5">
      <div className="rounded-lg border border-[var(--go-warm-border)] bg-background p-4">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
              Potential outcome
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant={outcomeMeta.badgeVariant} className="text-[11px]">
                {outcomeMeta.label}
              </Badge>
              <span className="text-[12px] font-medium text-foreground">
                {Math.round(outcome.confidence)}% confidence
              </span>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">Indicative only</p>
        </div>

        {compactRationale && (
          <p className="text-[13px] leading-[1.7] text-foreground">{compactRationale}</p>
        )}

        <div className="mt-4 rounded-lg border border-border bg-[var(--go-spring)] px-3 py-2">
          <p className="text-[11px] leading-[1.6] text-muted-foreground">
            This recommendation is guidance based on the available complaint record and supporting precedent.
            Final decisions may change if the evidence base changes.
          </p>
        </div>
      </div>
    </section>
  );
};

export default OutcomeSection;
